import { FC, useState, useEffect } from 'react';
import { Button, Radio, TextInput } from 'components/core';
import getButtonLabel from '../OrderCheckout/CurrentStep';
import { useDoctorProfile, useRetriveCart } from 'reactQuery/queries';

import {
  Styled,
  OrderAddressProps,
  RewardPointsWarningModal,
  isBillingAddressAvailable,
  isValidBillingAddressSelected,
  getNameFromAddress,
  getMailingNameFromAddress,
} from './';
import { Cart } from 'types';
import { useAddresses } from 'reactQuery/queries';
import { LoadingSpinner, LoadingWrapper } from 'styles/sharedStyles';
import {
  getSelectedBillingAddress,
  getSelectedShippingAddress,
  isValidEmailAddress,
} from 'components/OrderCheckout';
import { ServerStateKeysEnum } from 'reactQuery/constants';
import { useQueryClient } from 'react-query';
import { useI18n } from 'contexts/i18n';
import getConfig from 'next/config';

const OrderAddress: FC<OrderAddressProps> = ({
  mode,
  onSave,
  selectedShippingAddress,
  selectedBillingAddress,
  setBillingAddress,
  setShippingAddress,
  isSaveAddressMutationLoading,
  email,
  onEmailChange,
}) => {
  const [showRewardPointsWarningModal, setShowRewardPointsWarningModal] =
    useState(false);
  const i18n = useI18n();
  const queryClient = useQueryClient();
  const { publicRuntimeConfig } = getConfig();

  const [isValidEmail] = useState(email.length > 0);
  const { data: currentCart } = useRetriveCart();

  const currentOrder = queryClient.getQueryData<Cart>(
    ServerStateKeysEnum.userCurrentCart
  );
  const {
    data: address,
    isLoading: isAddressLoading,
    isFetching: isAddressFetching,
    isIdle,
    isError,
  } = useAddresses();

  const title = getButtonLabel(currentCart?.checkoutStates, 'address');

  useEffect(() => {
    if (address) {
      setBillingAddress(getSelectedBillingAddress(currentOrder, address));
      setShippingAddress(getSelectedShippingAddress(currentOrder, address));
    }
  }, [address, currentOrder, setBillingAddress, setShippingAddress]);

  if (isAddressLoading || isIdle || isAddressFetching) {
    return (
      <LoadingWrapper>
        <LoadingSpinner width="25px" height="25px" isPrimary={true} />
      </LoadingWrapper>
    );
  }
  if (isError) {
    return (
      <Styled.ErrorSection>
        {
          i18n[
            'Unable to fetch shipping and billing address, please try again later.'
          ]
        }
      </Styled.ErrorSection>
    );
  }

  const saveAddressHandler = () => {
    const storeRegion = publicRuntimeConfig.STORE_REGION;
    if (
      storeRegion === 'APAC' &&
      currentCart &&
      parseInt(currentCart.totalRedeemPoints) > 0
    ) {
      setShowRewardPointsWarningModal(true);
    } else {
      onSave();
    }
  };
  return (
    <>
      {mode === 'create' && (
        <Styled.OrderAddressesCreateContainer>
          <Styled.ShippingAddress>
            <Styled.Title>{i18n['Select Shipping Address']}</Styled.Title>
            <div>
              {address?.shippingAddresses.map(shippingAddress => {
                return (
                  <Styled.AddressContainer
                    key={shippingAddress.id}
                    isSelected={
                      shippingAddress.id === selectedShippingAddress?.id
                    }
                    onClick={() => setShippingAddress(shippingAddress)}
                  >
                    <Radio
                      checked={
                        shippingAddress.id === selectedShippingAddress?.id
                      }
                      onChange={() => setShippingAddress(shippingAddress)}
                      disabled={isSaveAddressMutationLoading}
                    />
                    <Styled.AddressWrapper>
                      {getNameFromAddress(shippingAddress).length > 0 ? (
                        <Styled.MailingName>
                          {getNameFromAddress(shippingAddress)}
                        </Styled.MailingName>
                      ) : (
                        ''
                      )}

                      <Styled.MailingName>
                        {getMailingNameFromAddress(shippingAddress)}
                      </Styled.MailingName>
                      <Styled.Address>
                        {shippingAddress.address1} {shippingAddress.address2}{' '}
                        {shippingAddress.address3}
                        <br />
                        {shippingAddress.city}, {shippingAddress.stateName}{' '}
                        {shippingAddress.zipcode}, {shippingAddress.countryIso}
                      </Styled.Address>
                    </Styled.AddressWrapper>
                  </Styled.AddressContainer>
                );
              })}
            </div>
          </Styled.ShippingAddress>

          {isBillingAddressAvailable(address.billingAddresses) && (
            <Styled.BillingAddress>
              <Styled.Title>{i18n['Select Billing Address']}</Styled.Title>

              {
                <div>
                  {address?.billingAddresses.map(billingAddress => {
                    return (
                      <Styled.AddressContainer
                        key={billingAddress.id}
                        isSelected={
                          billingAddress.id === selectedBillingAddress?.id
                        }
                        onClick={() => setBillingAddress(billingAddress)}
                      >
                        <Radio
                          checked={
                            billingAddress.id === selectedBillingAddress?.id
                          }
                          onChange={() => setBillingAddress(billingAddress)}
                          disabled={isSaveAddressMutationLoading}
                        />
                        <Styled.AddressWrapper>
                          {getNameFromAddress(billingAddress).length > 0 ? (
                            <Styled.MailingName>
                              {getNameFromAddress(billingAddress)}
                            </Styled.MailingName>
                          ) : (
                            ''
                          )}
                          <Styled.MailingName>
                            {getMailingNameFromAddress(billingAddress)}
                          </Styled.MailingName>
                          <Styled.Address>
                            {billingAddress.address1} {billingAddress.address2}{' '}
                            {billingAddress.address3}
                            <br />
                            {billingAddress.city}, {billingAddress.stateName}{' '}
                            {billingAddress.zipcode},{' '}
                            {billingAddress.countryIso}
                          </Styled.Address>
                        </Styled.AddressWrapper>
                      </Styled.AddressContainer>
                    );
                  })}
                </div>
              }
            </Styled.BillingAddress>
          )}

          <Styled.EmailAddress>
            <Styled.Title>{i18n['Email Address']}</Styled.Title>
            <Styled.EmailContainer>
              {!isValidEmail ? (
                <TextInput
                  value={email}
                  variant={'grey'}
                  width="100%"
                  onChange={onEmailChange}
                  isError={!isValidEmailAddress(email)}
                  size="md"
                />
              ) : (
                <TextInput
                  value={email}
                  variant={'grey'}
                  width="100%"
                  size="md"
                  disabled={true}
                  onChange={() => {
                    return;
                  }}
                />
              )}
            </Styled.EmailContainer>
          </Styled.EmailAddress>

          <Styled.ButtonWrapper>
            <Button
              size="md"
              onClick={saveAddressHandler}
              loading={isSaveAddressMutationLoading}
              disabled={
                isSaveAddressMutationLoading ||
                !isValidEmailAddress(email) ||
                !isValidBillingAddressSelected(
                  selectedBillingAddress,
                  address.billingAddresses
                ) ||
                typeof selectedShippingAddress?.id === 'undefined'
              }
            >
              {i18n[title]}
            </Button>
          </Styled.ButtonWrapper>
        </Styled.OrderAddressesCreateContainer>
      )}
      {mode === 'edit' && (
        <Styled.OrderAddressesEditContainer>
          <Styled.SelectedAddressContainer>
            <Styled.Title>{i18n['Shipping Address']}</Styled.Title>
            <Styled.SelectedAddress>
              {getNameFromAddress(selectedShippingAddress).length > 0 ? (
                <>
                  {getNameFromAddress(selectedShippingAddress)} <br />
                </>
              ) : (
                ''
              )}
              {getMailingNameFromAddress(selectedShippingAddress)} <br />
              {selectedShippingAddress?.address1}{' '}
              {selectedShippingAddress?.address2}{' '}
              {selectedShippingAddress?.address3}
              <br />
              <span>
                {selectedShippingAddress?.city},{' '}
                {selectedShippingAddress?.stateName}{' '}
                {selectedShippingAddress?.zipcode}
              </span>
            </Styled.SelectedAddress>
          </Styled.SelectedAddressContainer>

          {isBillingAddressAvailable(address.billingAddresses) && (
            <Styled.SelectedAddressContainer>
              <Styled.Title>{i18n['Billing Address']}</Styled.Title>
              <Styled.SelectedAddress>
                {getNameFromAddress(selectedBillingAddress).length > 0 ? (
                  <>
                    {getNameFromAddress(selectedBillingAddress)} <br />
                  </>
                ) : (
                  ''
                )}
                {getMailingNameFromAddress(selectedBillingAddress)}
                <br />
                {selectedBillingAddress?.address1}{' '}
                {selectedBillingAddress?.address2}{' '}
                {selectedBillingAddress?.address3}
                <br />
                <span>
                  {selectedBillingAddress?.city},{' '}
                  {selectedBillingAddress?.stateName}{' '}
                  {selectedBillingAddress?.zipcode}
                </span>
              </Styled.SelectedAddress>
            </Styled.SelectedAddressContainer>
          )}

          <Styled.SelectedAddressContainer>
            <Styled.Title>{i18n['Email Address']}</Styled.Title>
            <Styled.SelectedAddress>{email}</Styled.SelectedAddress>
          </Styled.SelectedAddressContainer>
        </Styled.OrderAddressesEditContainer>
      )}
      {showRewardPointsWarningModal && (
        <RewardPointsWarningModal
          onSave={onSave}
          onClose={() => setShowRewardPointsWarningModal(false)}
          selectedShippingAddress={selectedShippingAddress}
        />
      )}
    </>
  );
};

export default OrderAddress;
